import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import { ApiCallError } from "web_myplann/Infrastructure/Api/ApiUtils";
import {
	getApiUrl,
	getChatViewImageIdPath,
} from "web_myplann/Infrastructure/ApiOld/Api";
import { sessionState } from "web_myplann/Shared/State/sessionAtom";

const ATTACHMENT_IMAGE_GC_TIME = 10 * 60 * 1000;

export const useChatAttachmentImage = (
	attachmentId: number,
	enabled: boolean,
) => {
	const { token, tokenVerify, authorizationType } = useAtomValue(sessionState);

	const { data: blob, isLoading } = useQuery({
		queryKey: ChatKeys.attachmentImage(attachmentId),
		queryFn: async () => {
			const url = `${getApiUrl()}${getChatViewImageIdPath(attachmentId)}`;

			const headers = new Headers();
			headers.set("Authorization", `Bearer ${token}`);
			headers.set("token-verify", tokenVerify);
			if (authorizationType) {
				headers.set("Authorization-type", authorizationType);
			}

			const options: RequestInit = { headers };
			const response = await fetch(url, options).catch(() => null);

			if (!response?.ok) {
				throw new ApiCallError(
					`Chat attachment image ${attachmentId} could not be loaded`,
					{
						data: null,
						error: new Error(response?.statusText ?? "Network error"),
						status: 0,
						args: { url, options },
					},
					{ skipToastFor: true },
					ChatKeys.attachmentImage(attachmentId),
				);
			}

			return response.blob();
		},
		enabled,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: ATTACHMENT_IMAGE_GC_TIME,
		meta: { persist: false },
	});

	const [url, setUrl] = useState<string>();

	useEffect(() => {
		if (!blob) {
			setUrl(undefined);
			return;
		}

		const objectUrl = URL.createObjectURL(blob);
		setUrl(objectUrl);

		return () => URL.revokeObjectURL(objectUrl);
	}, [blob]);

	return { url, isLoading };
};
